import { XAxis, Tooltip, AreaChart, Area, ResponsiveContainer } from 'recharts';
import ColoredDotsDescription from '../ColoredDotsDescription';
import { Box, HStack, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { WELLNESS_DATA_MULTIPLIER, WELLNESS_DOMAINS_DISPLAY_LABELS, TRENDS_CHART_DOMAINS_LIST } from '@/constants';
import { CHART_COLORS_ARRAY } from '@/constants/ui';
import { THealthDomain } from '@/types/Health.types';
import { formatDate } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import colors from '@/theme/colors';
import Dropdown from '@/components/atoms/Dropdown';
import { TDashboardTrend } from '@/types/Dashboard.types';
import { TOrganizationDashboardProgress } from '@/types/Organization.types';

const TRENDS_CHART_HEIGHT = 264;

interface Props {
  dashboardTrendData: Partial<TDashboardTrend> | null;
  progressData?: TOrganizationDashboardProgress[];
  preSelectedDomain?: THealthDomain;
  chartHeight?: number;
  preMappedData?: TChartData;
  dotsHidden?: boolean;
}

const NO_DATA_PLACEHOLDER = [
  {
    month: 'Dec',
    purpose: 18,
    spiritual: 36,
    vocational: 94,
  },
  {
    month: 'Jan',
    purpose: 28,
    spiritual: 46,
    vocational: 74,
  },
  {
    month: 'Feb',
    purpose: 18,
    spiritual: 36,
    vocational: 84,
  },
  {
    month: 'Mar',
    purpose: 23,
    spiritual: 43,
    vocational: 87,
  },
  {
    month: 'Apr',
    purpose: 16,
    spiritual: 32,
    vocational: 76,
  },
  {
    month: 'May',
    purpose: 38,
    spiritual: 51,
    vocational: 86,
  },
  {
    month: 'Jun',
    purpose: 30,
    spiritual: 45,
    vocational: 75,
  },
  {
    month: 'Jul',
    purpose: 38,
    spiritual: 53,
    vocational: 93,
  },
  {
    month: 'Aug',
    purpose: 38,
    spiritual: 63,
    vocational: 73,
  },
];

type TChartData = {
  month: string;
  purpose: number;
  spiritual: number;
  vocational: number;
  mental: number;
  physical: number;
  fun: number;
  social: number;
  totalScore: number;
  screenerCount?: number;
  screenerCountUnique?: number;
  screenerActivityPercentage?: number;
}[];

type TTrendChartDomainOption = THealthDomain | 'total' | 'all';
const TRENDS_CHART_DOMAINS_DROPDOWN_OPTIONS: TTrendChartDomainOption[] = [...TRENDS_CHART_DOMAINS_LIST, 'total', 'all'];

const CustomizedGroupTick = (props: any) => {
  const { x, y, payload } = props;

  return (
    <g color={colors.text.mediumBlue}>
      <text x={x} y={y + 15} fontWeight={600} fill={colors.text.mediumBlue}>
        {payload.value}
      </text>
    </g>
  );
};

const CustomTooltip = ({ payload, label, active }: any) => {
  if (active && label) {
    const fullRow = payload?.[0]?.payload as
      | { screenerCount?: number; screenerActivityPercentage?: number }
      | undefined;
    const screenerCount = fullRow?.screenerCount;
    const activityPercentage = fullRow?.screenerActivityPercentage;
    const hasActivityValue = activityPercentage !== undefined;

    return (
      <VStack
        gap={'6px'}
        align={'start'}
        borderRadius={'10px'}
        backgroundColor={'extra.white'}
        padding={'15px'}
        zIndex={3}
        boxShadow="0px 4px 40px 0px #00417926"
      >
        <VStack gap={'2px'} alignItems={'flex-start'}>
          <Text variant="urbanistSemiBoldRegular" color={'text.mediumGray'} fontSize={'14px'}>
            {label}
          </Text>
          {screenerCount !== undefined && (
            <Text variant={'urbanistBold'} color={'text.darkBlue'} fontWeight={800} fontSize={'12px'}>
              {screenerCount} {screenerCount === 1 ? 'Screener' : 'Screeners'}
            </Text>
          )}
        </VStack>
        {payload.map((payloadItem: any) => (
          <HStack key={payloadItem.name}>
            <VStack borderRadius={'50%'} width={'12px'} height={'12px'} backgroundColor={payloadItem.stroke} />
            <Text
              variant={'urbanistSemiBoldRegular'}
            >{`${payloadItem.name === 'totalScore' ? 'Combined Score' : WELLNESS_DOMAINS_DISPLAY_LABELS[payloadItem.name as THealthDomain]}: ${payloadItem.value.toFixed(0)}`}</Text>
          </HStack>
        ))}
        {hasActivityValue && (
          <HStack paddingY={'4px'} borderRadius={'4px'} gap={'6px'}>
            <Text variant={'urbanistSmallBold'} color={'text.mediumGray'} fontWeight={800}>
              SCREENER ACTIVITY:
            </Text>
            <Text variant={'urbanistSmallBold'} color={'text.mediumGray'} fontWeight={800}>
              {activityPercentage}%
            </Text>
          </HStack>
        )}
      </VStack>
    );
  }

  return null;
};

const displayDropdownOption = (option: string) => {
  if (option === 'all') return 'All Domains';
  if (option === 'total') return 'Combined Score';

  return `${WELLNESS_DOMAINS_DISPLAY_LABELS[option as THealthDomain]} Wholeness`;
};

const WholenessTrends = ({
  dashboardTrendData,
  progressData,
  preSelectedDomain,
  chartHeight = TRENDS_CHART_HEIGHT,
  preMappedData,
  dotsHidden = true,
}: Props) => {
  const defaultDomainForDataExtraction = dashboardTrendData?.[preSelectedDomain ?? 'mental'];

  const mappedDashTrendData = useMemo(() => {
    const monthsData = dashboardTrendData
      ? (defaultDomainForDataExtraction?.map((monthScoreInfo) => monthScoreInfo.month) ?? [])
      : [];

    if ((dashboardTrendData && monthsData.length) || preMappedData) {
      const newMappedDashTrendData = preMappedData
        ? preMappedData
        : monthsData.map((monthData, i) => ({
            month: formatDate(new Date(2025, monthData - 1), 'MMM'),
            purpose: (dashboardTrendData?.purpose?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            spiritual: (dashboardTrendData?.spiritual?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            vocational: (dashboardTrendData?.vocational?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            mental: (dashboardTrendData?.mental?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            physical: (dashboardTrendData?.physical?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            fun: (dashboardTrendData?.fun?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            social: (dashboardTrendData?.social?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            totalScore: (dashboardTrendData?.totalScore?.[i].score ?? 0) * WELLNESS_DATA_MULTIPLIER,
            screenerCount: progressData?.[i]?.screenerCount,
            screenerCountUnique: progressData?.[i]?.screenerCountUnique,
            screenerActivityPercentage: progressData?.[i]?.screenerActivityPercentage,
          }));

      if (
        preMappedData
          ? newMappedDashTrendData.length < preMappedData.length + 2
          : newMappedDashTrendData.length < monthsData.length + 2
      ) {
        // Adding one month to the end with repeated last values to make the chart look good
        newMappedDashTrendData.push({
          month: '',
          fun: newMappedDashTrendData[newMappedDashTrendData.length - 1].fun,
          mental: newMappedDashTrendData[newMappedDashTrendData.length - 1].mental,
          physical: newMappedDashTrendData[newMappedDashTrendData.length - 1].physical,
          purpose: newMappedDashTrendData[newMappedDashTrendData.length - 1].purpose,
          social: newMappedDashTrendData[newMappedDashTrendData.length - 1].social,
          spiritual: newMappedDashTrendData[newMappedDashTrendData.length - 1].spiritual,
          totalScore: newMappedDashTrendData[newMappedDashTrendData.length - 1].totalScore,
          vocational: newMappedDashTrendData[newMappedDashTrendData.length - 1].vocational,
        });

        // Adding one month to the beginning with repeated first values to make the chart look good
        newMappedDashTrendData?.unshift({
          month: '',
          fun: newMappedDashTrendData[0].fun,
          mental: newMappedDashTrendData[0].mental,
          physical: newMappedDashTrendData[0].physical,
          purpose: newMappedDashTrendData[0].purpose,
          social: newMappedDashTrendData[0].social,
          spiritual: newMappedDashTrendData[0].spiritual,
          totalScore: newMappedDashTrendData[0].totalScore,
          vocational: newMappedDashTrendData[0].vocational,
        });
      }

      return newMappedDashTrendData;
    }

    return [];
  }, [dashboardTrendData, preSelectedDomain, progressData, preMappedData]);

  const domainDropdownDisclosure = useDisclosure();

  const [selectedWellnessDomains, setSelectedWellnessDomains] = useState<Set<TTrendChartDomainOption>>(
    preSelectedDomain ? new Set([preSelectedDomain]) : new Set(['all']),
  );

  const updateSelectedWellnessDomains = useCallback((newOption: TTrendChartDomainOption) => {
    setSelectedWellnessDomains((prev) => {
      const newSelectedWellnessDomains = new Set(prev);

      if (newOption === 'all') {
        newSelectedWellnessDomains.clear();
        newSelectedWellnessDomains.add('all');
      } else {
        if (newSelectedWellnessDomains.has(newOption)) {
          newSelectedWellnessDomains.delete(newOption);
          if (newSelectedWellnessDomains.size === 0) {
            newSelectedWellnessDomains.add('all');
          }
        } else {
          newSelectedWellnessDomains.add(newOption);
          newSelectedWellnessDomains.delete('all');
        }
      }

      return newSelectedWellnessDomains;
    });
  }, []);

  const noData =
    dashboardTrendData === null || (defaultDomainForDataExtraction && defaultDomainForDataExtraction.length <= 1);

  return (
    <VStack gap={'30px'} width={'100%'} position={'relative'}>
      <VStack
        display={noData ? 'flex' : 'none'}
        position={'absolute'}
        width={'100%'}
        height={'100%'}
        zIndex={3}
        backgroundColor={`${colors.background.lightBlue}80`}
        justify={'center'}
      >
        <VStack
          height={'80px'}
          width={'400px'}
          padding={'20px'}
          borderRadius={'12px'}
          backgroundColor={'extra.white'}
          border={`1px solid ${colors.text.mediumGray}`}
          boxShadow="0px 4px 20px 0px #00417926"
        >
          <Text variant={'urbanistMediumLarge'}>
            Once you complete more screeners and time passes we will show graphs for each domain here.
          </Text>
        </VStack>
      </VStack>

      <HStack width={'100%'} justifyContent={'space-between'} alignItems={'flex-start'}>
        <VStack align={'start'} gap={'10px'}>
          <Text variant="loraSectionTitle" textAlign={'start'}>
            Wholeness Trends
          </Text>
          <Text variant={'urbanistMediumRegular'} color={'text.mediumGray'} marginBottom={'25px'}>
            Historical data of domain score averages per month.
          </Text>
        </VStack>

        {!preSelectedDomain && (
          <VStack>
            <Dropdown<TTrendChartDomainOption>
              disclosure={domainDropdownDisclosure}
              title="DOMAINS"
              options={TRENDS_CHART_DOMAINS_DROPDOWN_OPTIONS}
              optionLabelDisplay={displayDropdownOption}
              selectedOptions={Array.from(selectedWellnessDomains.values())}
              onOptionClick={(option) => updateSelectedWellnessDomains(option)}
            />
          </VStack>
        )}
      </HStack>

      {/* Chart */}
      <HStack width={'100%'} alignItems={'start'} paddingTop={'10px'} opacity={noData ? 0.5 : 1}>
        <Box height={chartHeight * 0.5} zIndex={1} boxShadow={`0px 10px 30px 50px ${colors.background.lightBlue}`} />

        <ResponsiveContainer width={'110%'} height={chartHeight}>
          <AreaChart
            width={500}
            height={chartHeight}
            data={noData ? NO_DATA_PLACEHOLDER : mappedDashTrendData}
            margin={{
              top: 10,
              right: -20,
              left: -20,
              bottom: 0,
            }}
          >
            <XAxis
              tick={<CustomizedGroupTick />}
              dataKey="month"
              dy={5}
              tickLine={false}
              stroke={`${colors.text.mediumGray}80`}
            />

            <Tooltip content={<CustomTooltip />} wrapperStyle={{ zIndex: 3 }} />

            <defs>
              {TRENDS_CHART_DOMAINS_LIST.map((domain, i) => (
                <linearGradient key={domain} id={domain} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS_ARRAY[i]} stopOpacity={0.5} />
                  <stop offset="95%" stopColor={colors.background.lightBlue} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>

            {TRENDS_CHART_DOMAINS_LIST.map((domain, i) => {
              if (selectedWellnessDomains.has(domain) || selectedWellnessDomains.has('all')) {
                return (
                  <Area
                    key={domain}
                    type="linear"
                    dataKey={domain}
                    stackId={i + 1}
                    stroke={CHART_COLORS_ARRAY[i]}
                    strokeWidth={2}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    fill={`url(#${domain})`}
                    connectNulls
                    strokeDasharray={noData ? 10 : 0}
                    activeDot={{
                      r: 5,
                      strokeWidth: 2,
                      stroke: colors.extra.white,
                      fill: CHART_COLORS_ARRAY[i],
                    }}
                  />
                );
              }
            })}

            {(selectedWellnessDomains.has('total') || selectedWellnessDomains.has('all')) && (
              <Area
                type="linear"
                dataKey="totalScore"
                stackId="total-score"
                stroke={colors.text.mediumBlue}
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
                fillOpacity={0}
                strokeDasharray={10}
                activeDot={{
                  r: 5,
                  strokeWidth: 2,
                  stroke: colors.extra.white,
                  fill: colors.text.mediumBlue,
                }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>

        <Box height={chartHeight * 0.8} zIndex={1} boxShadow={`0px 10px 30px 50px ${colors.background.lightBlue}`} />
      </HStack>

      {!preSelectedDomain && !dotsHidden && (
        <ColoredDotsDescription
          dots={TRENDS_CHART_DOMAINS_LIST.map((domain, i) => ({
            color: CHART_COLORS_ARRAY[i],
            description: WELLNESS_DOMAINS_DISPLAY_LABELS[domain],
          }))}
          dotsGap="20px"
        />
      )}
    </VStack>
  );
};

export default WholenessTrends;
